// Every feature flag key with its lifecycle metadata. No imports and erasable syntax only:
// plain Node scripts load this file through native type stripping.

/** Why a flag exists, which decides whether it has to be removed. */
export const FeatureFlagType = {
  RELEASE: 'release',
  OPS: 'ops',
  PERMISSION: 'permission',
  EXPERIMENT: 'experiment'
} as const;

export type FeatureFlagType = (typeof FeatureFlagType)[keyof typeof FeatureFlagType];

/** Every Flipt flag key the code evaluates; a key missing from Flipt evaluates to disabled. */
export const FeatureFlag = {
  /** A master account registering Bridges of its own; evaluated with `{ masterAccountId }`. */
  MASTER_ACCOUNT_BRIDGES: 'master-account-bridges',
  /** A master account's integration templates; evaluated with `{ masterAccountId }`. */
  MASTER_ACCOUNT_INTEGRATION_TEMPLATES: 'master-account-integration-templates',
  /** Routes document classification through the plugin system instead of the AI agent. */
  DOCUMENT_CLASSIFICATION_PLUGIN: 'document-classification-plugin',
  /** Routes document extraction through a plugin on Temporal instead of the BullMQ agent chain. */
  DOCUMENT_EXTRACTION_PLUGIN: 'document-extraction-plugin',
  /** Direction runs of a bridged connector queue for their Bridge; evaluated per Bridge id. */
  BRIDGE_RUN_SCHEDULER: 'bridge-run-scheduler',
  /** P2P: procurement nav and pages (incl. receipts), the number series tab, the P2P lifecycles. */
  PROCUREMENT: 'p2p-procurement',
  /** P2P: contracts — the contracts list and detail, its tab in the document lists, contract fields. */
  CONTRACTS: 'p2p-contracts',
  /** Reading ABO/GPC bank statements; evaluated per account, off by default. */
  BANK_STATEMENTS_GPC: 'bank-statements-gpc',
  /** Payments: the bank movements nav and pages, the payments panels and recording a payment. */
  PAYMENTS: 'payments',
  /** Turnstile CAPTCHA on signup; evaluated per client IP. */
  SIGNUP_CAPTCHA: 'signup-captcha-enabled',
  /** Per-IP and per-email signup rate limits; evaluated per client IP. */
  SIGNUP_RATE_LIMIT: 'signup-rate-limit-enabled',
  /** Disposable-domain and e-mail alias checks on signup; evaluated per client IP. */
  SIGNUP_EMAIL_HARDENING: 'signup-email-hardening-enabled'
} as const;

export type FeatureFlagKeyValue = (typeof FeatureFlag)[keyof typeof FeatureFlag];

/** A calendar date written as YYYY-MM-DD. */
export type FeatureFlagDate = `${number}-${number}-${number}`;

interface FeatureFlagMetadataBase {
  /** GitHub login of the owner. */
  owner: string;
  createdAt: FeatureFlagDate;
}

/** A release or experiment flag; it has to be gone after `removeAfter`. */
export interface ExpiringFeatureFlagMetadata extends FeatureFlagMetadataBase {
  type: typeof FeatureFlagType.RELEASE | typeof FeatureFlagType.EXPERIMENT;
  removeAfter: FeatureFlagDate;
}

/** An ops or permission flag; it stays, so it has no removal date. */
export interface PermanentFeatureFlagMetadata extends FeatureFlagMetadataBase {
  type: typeof FeatureFlagType.OPS | typeof FeatureFlagType.PERMISSION;
  removeAfter: null;
}

/** Who owns a flag, what kind it is and when it has to be gone. */
export type FeatureFlagMetadata = ExpiringFeatureFlagMetadata | PermanentFeatureFlagMetadata;

/** Lifecycle metadata of every key in `FeatureFlag`. */
export const FEATURE_FLAG_METADATA = {
  [FeatureFlag.MASTER_ACCOUNT_BRIDGES]: {
    owner: 'jkaramon',
    type: FeatureFlagType.RELEASE,
    createdAt: '2026-10-08',
    removeAfter: '2026-12-07'
  },
  [FeatureFlag.MASTER_ACCOUNT_INTEGRATION_TEMPLATES]: {
    owner: 'jkaramon',
    type: FeatureFlagType.RELEASE,
    createdAt: '2026-10-13',
    removeAfter: '2026-12-12'
  },
  [FeatureFlag.DOCUMENT_CLASSIFICATION_PLUGIN]: {
    owner: 'pavelondrousek',
    type: FeatureFlagType.RELEASE,
    createdAt: '2026-09-03',
    removeAfter: '2026-11-02'
  },
  [FeatureFlag.DOCUMENT_EXTRACTION_PLUGIN]: {
    owner: 'pavelondrousek',
    type: FeatureFlagType.RELEASE,
    createdAt: '2026-09-09',
    removeAfter: '2026-11-08'
  },
  [FeatureFlag.BRIDGE_RUN_SCHEDULER]: {
    owner: 'jkaramon',
    type: FeatureFlagType.RELEASE,
    createdAt: '2026-10-08',
    removeAfter: '2026-12-07'
  },
  [FeatureFlag.PROCUREMENT]: {
    owner: 'DanielHaas21',
    type: FeatureFlagType.RELEASE,
    createdAt: '2026-09-09',
    removeAfter: '2026-11-08'
  },
  [FeatureFlag.CONTRACTS]: {
    owner: 'DanielHaas21',
    type: FeatureFlagType.RELEASE,
    createdAt: '2026-10-19',
    removeAfter: '2026-12-18'
  },
  [FeatureFlag.BANK_STATEMENTS_GPC]: {
    owner: 'jkaramon',
    type: FeatureFlagType.RELEASE,
    createdAt: '2026-10-20',
    removeAfter: '2026-12-19'
  },
  [FeatureFlag.PAYMENTS]: {
    owner: 'jkaramon',
    type: FeatureFlagType.RELEASE,
    createdAt: '2026-10-21',
    removeAfter: '2026-12-20'
  },
  [FeatureFlag.SIGNUP_CAPTCHA]: {
    owner: 'svoza10',
    type: FeatureFlagType.RELEASE,
    createdAt: '2026-07-01',
    removeAfter: '2026-08-30'
  },
  [FeatureFlag.SIGNUP_RATE_LIMIT]: {
    owner: 'svoza10',
    type: FeatureFlagType.RELEASE,
    createdAt: '2026-07-01',
    removeAfter: '2026-08-30'
  },
  [FeatureFlag.SIGNUP_EMAIL_HARDENING]: {
    owner: 'svoza10',
    type: FeatureFlagType.RELEASE,
    createdAt: '2026-07-01',
    removeAfter: '2026-08-30'
  }
} as const satisfies Record<FeatureFlagKeyValue, FeatureFlagMetadata>;
